import React, { createContext, useEffect, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { Button, Card, CardBody, CardHeader, Col, Form, FormGroup, Input, Row } from 'reactstrap'
import { ToastContainer } from 'react-toastify'

import FileInput from '../../components/molecules/FileInput'
import { useFileUpload } from '../../hooks'
import Email from '../../main/FormFields/Email'
import { notify } from '../../utils/notify'
import GPTEstimateModal from '../../components/molecules/GPTEstimateModal'
import ClienteSelect from '../../components/molecules/ClienteSelect'
import { api } from '../../utils'

export const EmailContext = createContext();

const KeywordIntentionsForm = ({ onComplete }) => {
  const { upload, loading } = useFileUpload();
  const [tableName, setTableName] = useState('');
  const [file, setFile] = useState(null);
  const [emails, setEmails] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [showEstimate, setShowEstimate] = useState(false);
  const [estimateParams, setEstimateParams] = useState({ n_keywords: 0, total_tokens: 0, estimate: 0 });
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const resetForm = () => {
    setTableName('');
    setFile(null);
    setEmails([]);
    setSelectedClientId('');
  };

  const performSubmit = async () => {
    const formData = new FormData();
    formData.append('table_name', tableName.trim());
    formData.append('file', file);
    formData.append('mail', emails);
    formData.append('cliente', selectedClientId);

    try {
      await upload('post', 'keyword-intentions/', formData, {
        onSuccess: () => {
          notify('Richiesta inviata. Riceverai una mail quando il report sarà pronto.', 'success');
          resetForm();
          if (onComplete) {
            onComplete();
          }
        },
        onError: (errorMessage) => {
          notify(errorMessage, 'error');
        },
      });
    } catch (err) {
      console.error('Keyword Intentions failed:', err);
    }
  };

  const handleEstimate = async (deferSubmit = false) => {
    if (!file) {
      notify('Carica prima un file per stimare i costi.', 'warning');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const data = await upload('post', 'keyword-intentions-estimate', formData, {
        onSuccess: () => {},
        onError: (errorMessage) => {
          notify(errorMessage, 'error');
        },
      });

      if (data) {
        const estimateNumber = parseFloat((data.cost_min || '').replace('$', '').replace(',', '.') || 0);
        setEstimateParams({
          n_keywords: data.n_keywords || 0,
          total_tokens: (data.n_keywords || 0) * 200, // approx prompt+response per kw
          estimate: estimateNumber.toFixed(4),
        });
        setPendingSubmit(deferSubmit);
        setShowEstimate(true);
      }
    } catch (err) {
      console.error('Estimate failed:', err);
    }
  };

  const handleSubmit = async (event) => {
    if (event) {
      event.preventDefault();
    }

    if (!tableName.trim() || !file || emails.length === 0) {
      notify('Compila il nome della tabella, seleziona un file e inserisci almeno una email.', 'error');
      return;
    }
    if (!selectedClientId) {
      notify('Seleziona un cliente.', 'error');
      return;
    }

    await handleEstimate(true);
  };

  useEffect(() => {
    api
      .get('clienti/')
      .then((response) => {
        setClienti(Array.isArray(response.data) ? response.data : response.data?.results || [])
      })
      .catch((err) => {
        console.error('Failed to fetch clienti', err)
      })
  }, [])

  return (
    <>
      {loading ? (
        <Spinner animation="grow" />
      ) : (
        <Row>
          <div className="col pb-5">
            <Card className="bg-secondary shadow">
              <CardHeader className="bg-white border-0">
                <Row>
                  <Col md={12}>
                    <h1 className="mb-0">Keyword Intentions</h1>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <ClienteSelect
                        id="keyword-intentions-cliente"
                        label="Cliente canonico"
                        clienti={clienti}
                        selectedClientId={selectedClientId}
                        onChange={setSelectedClientId}
                        isRequired
                        showCreateLink
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <label htmlFor="table-name" className="form-control-label">
                          Nome tabella su BigQuery
                        </label>
                        <Input
                          id="table-name"
                          type="text"
                          className="form-control-alternative"
                          placeholder="es. intent_report_maggio"
                          value={tableName}
                          onChange={(event) => setTableName(event.target.value)}
                          required
                        />
                      </FormGroup>
                      <FileInput
                        onFileChange={setFile}
                        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                        helpText={
                          <>
                            Carica un file Excel con una colonna chiamata <b>Keyword</b>.
                          </>
                        }
                        required
                      />
                    </Col>
                    <Col md={6}>
                      <EmailContext.Provider value={{ emails, setEmails }}>
                        <Email context={EmailContext} />
                      </EmailContext.Provider>
                    </Col>
                  </Row>
                  <Row style={{ display: 'flex', justifyContent: 'flex-end', marginRight: '50px' }}>
                    <Button color="info" size="lg" onClick={handleSubmit}>
                      Avvia analisi
                    </Button>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </div>
        </Row>
      )}
      <ToastContainer />
      <GPTEstimateModal
        show={showEstimate}
        onHide={() => {
          setPendingSubmit(false);
          setShowEstimate(false);
        }}
        onSubmit={async () => {
          setShowEstimate(false);
          if (pendingSubmit) {
            await performSubmit();
            setPendingSubmit(false);
          }
        }}
        params={estimateParams}
      />
    </>
  );
};

export default KeywordIntentionsForm;
